import { useMemo, useState } from 'react';
import { Plus, Trash, X, MagnifyingGlass, Check } from '@phosphor-icons/react';
import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { toast } from 'react-toastify';
import { RenoTag, useRenoTags } from '../hooks/useRenoTags';
import { getRenoCategoryStyle } from '../utils/renoCategoryColor';

interface Props {
  /** De categorieën die op dit ticket staan (namen uit de gedeelde lijst). */
  tags: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
}

/**
 * Zelfde normalisatie als backend-side (`normalizeTagName`): getrimd, enkele spaties,
 * **met behoud van hoofdletters** — de categorieën van projectonderdelen staan met
 * hoofdletter ("Afbraakwerken"). Vergelijken gebeurt hoofdletterongevoelig.
 */
const normalize = (raw: string) => raw.trim().replace(/\s+/g, ' ');

/**
 * Categorieën op een renovatieticket: je **kiest** uit de gedeelde lijst
 * (zie {@link useRenoTags}) in plaats van vrij te typen.
 *
 * Dat is **dezelfde lijst** als de categorieën van een projectonderdeel. Eerder had het
 * ticket zijn eigen lijst, waardoor "afbraak" op een ticket en "Afbraakwerken" op een
 * onderdeel naast elkaar bestonden en je ze nooit samen kon filteren.
 *
 * Een nieuwe categorie aanmaken kan hier ook — die is dan ook in de renovatiemodule
 * kiesbaar. Verwijderen haalt ze van alle tickets waar ze op staat, met een bevestiging
 * die zegt hoeveel tickets dat zijn. Staat ze op **projectonderdelen**, dan kan je ze hier
 * niet verwijderen: dat zou een categorie weghalen waar een heel project op gebouwd is.
 */
export function RenoTagsInput({ tags, onChange, disabled }: Props) {
  const { tags: catalog, isLoading, createTag, deleteTag } = useRenoTags();
  const [isOpen, setIsOpen] = useState(false);
  const [zoek, setZoek] = useState('');
  const [tagToDelete, setTagToDelete] = useState<RenoTag | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const term = normalize(zoek);
  const termLower = term.toLowerCase();
  const zichtbaar = useMemo(
    () => (termLower ? catalog.filter((t) => t.name.toLowerCase().includes(termLower)) : catalog),
    [catalog, termLower],
  );
  // Enkel aanmaken wanneer de naam nog niet bestaat — anders kies je de bestaande gewoon.
  const kanAanmaken = !!term && !catalog.some((t) => t.name.toLowerCase() === termLower);

  const toggleTag = (name: string) => {
    onChange(tags.includes(name) ? tags.filter((t) => t !== name) : [...tags, name]);
  };

  const handleCreate = async () => {
    const created = await createTag(term);
    if (!created) return;
    if (!tags.includes(created.name)) onChange([...tags, created.name]);
    setZoek('');
  };

  const handleDelete = async () => {
    if (!tagToDelete) return;
    setIsDeleting(true);
    const result = await deleteTag(tagToDelete);
    setIsDeleting(false);
    if (!result) return;

    // Ook lokaal van dit ticket halen: de backend heeft ze overal weggehaald.
    if (tags.includes(tagToDelete.name)) onChange(tags.filter((t) => t !== tagToDelete.name));
    toast.success(
      result.removedFromTickets > 0
        ? `Categorie "${tagToDelete.name}" verwijderd (van ${result.removedFromTickets} ticket${
            result.removedFromTickets === 1 ? '' : 's'
          })`
        : `Categorie "${tagToDelete.name}" verwijderd`,
    );
    setTagToDelete(null);
  };

  return (
    <div className="px-4 py-3 space-y-1">
      <Label className="text-xs text-gray-500">Categorie</Label>

      <div className="flex flex-wrap items-center gap-1">
        {/* Zelfde kleur per categorie als in de lijst en op een helpdeskticket. */}
        {tags.map((tag) => {
          const { icon, bgColor, textColor } = getRenoCategoryStyle(tag);
          return (
            <span
              key={tag}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${bgColor} ${textColor}`}
            >
              {icon}
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => toggleTag(tag)}
                  title="Van dit ticket halen"
                  className="opacity-70 hover:opacity-100"
                >
                  <X size={10} weight="bold" />
                </button>
              )}
            </span>
          );
        })}

        {!disabled && (
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-6 gap-1 px-2 text-xs">
                <Plus size={10} weight="bold" />
                Categorie kiezen
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-0">
              <div className="border-b border-neutral-primary/10 p-2">
                <div className="relative">
                  <MagnifyingGlass
                    size={12}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-secondary-mid"
                  />
                  <Input
                    value={zoek}
                    onChange={(e) => setZoek(e.target.value)}
                    placeholder="Zoek of typ een nieuwe categorie..."
                    className="h-7 pl-6 text-xs"
                  />
                </div>
              </div>

              <div className="max-h-56 overflow-y-auto py-1">
                {isLoading && catalog.length === 0 && (
                  <p className="px-3 py-2 text-xs text-neutral-secondary-mid">Categorieën laden...</p>
                )}
                {!isLoading && zichtbaar.length === 0 && !kanAanmaken && (
                  <p className="px-3 py-2 text-xs text-neutral-secondary-mid">
                    Geen categorieën gevonden
                  </p>
                )}
                {zichtbaar.map((tag) => {
                  const isGekozen = tags.includes(tag.name);
                  // Staat de categorie op projectonderdelen, dan mag ze hier niet weg.
                  const inGebruikOpOnderdelen = (tag.partCount ?? 0) > 0;
                  return (
                    <div
                      key={tag.id}
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-neutral-primary/5"
                    >
                      <button
                        type="button"
                        onClick={() => toggleTag(tag.name)}
                        title={`${tag.name} ${isGekozen ? 'van dit ticket halen' : 'op dit ticket zetten'}`}
                        className="flex flex-1 items-center gap-2 min-w-0 text-left text-xs"
                      >
                        <span
                          className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                            isGekozen
                              ? 'border-moss-primary bg-moss-primary text-white'
                              : 'border-muted-foreground/40'
                          }`}
                        >
                          {isGekozen && <Check size={9} weight="bold" />}
                        </span>
                        {/* Kleurstip vooraan: dezelfde kleur als de chip krijgt, zodat
                            je in de lijst herkent wat je aanvinkt. */}
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${getRenoCategoryStyle(tag.name).bgColor}`}
                        />
                        <span className="truncate text-neutral-primary">{tag.name}</span>
                        {/* Soort categorie, zoals in de kiezer van een projectonderdeel. */}
                        {tag.type && (
                          <span className="shrink-0 text-[10px] text-neutral-secondary-mid">
                            {tag.type}
                          </span>
                        )}
                        {/* Hoe vaak ze gebruikt wordt: helpt bij het opruimen. */}
                        <span className="ml-auto shrink-0 text-[10px] text-neutral-secondary-mid">
                          {tag.ticketCount}
                        </span>
                      </button>
                      <button
                        type="button"
                        disabled={inGebruikOpOnderdelen}
                        onClick={() => {
                          // Popover sluiten vóór de bevestiging: twee lagen met een
                          // focus-trap over elkaar laat de dialoog niet meer sluiten.
                          setIsOpen(false);
                          setTagToDelete(tag);
                        }}
                        title={
                          inGebruikOpOnderdelen
                            ? `Staat op ${tag.partCount} projectonderdeel(en) — verwijderen kan enkel in de renovatiemodule`
                            : 'Categorie uit de lijst verwijderen'
                        }
                        className="shrink-0 text-neutral-secondary-mid hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-neutral-secondary-mid"
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {kanAanmaken && (
                <div className="border-t border-neutral-primary/10 p-2">
                  <Button
                    size="sm"
                    onClick={handleCreate}
                    className="h-7 w-full gap-1 bg-moss-primary text-xs text-white hover:bg-moss-primary/90"
                  >
                    <Plus size={10} weight="bold" />
                    "{term}" toevoegen aan de lijst
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}

        {tags.length === 0 && disabled && (
          <span className="text-xs italic text-neutral-secondary-mid">Geen categorie</span>
        )}
      </div>

      <p className="text-xs text-gray-400">
        Dezelfde categorieën als bij een projectonderdeel. Wat je hier toevoegt, is ook
        daar kiesbaar.
      </p>

      <AlertDialog
        open={!!tagToDelete}
        onOpenChange={(open) => { if (!open) setTagToDelete(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Categorie "{tagToDelete?.name}" verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              {tagToDelete && tagToDelete.ticketCount > 0
                ? `De categorie verdwijnt uit de lijst én van ${tagToDelete.ticketCount} ticket${
                    tagToDelete.ticketCount === 1 ? '' : 's'
                  } waar ze nu op staat. Dit kan niet ongedaan gemaakt worden.`
                : 'De categorie verdwijnt uit de lijst. Ze staat op geen enkel ticket.'}
              {' '}
              Het is dezelfde lijst als bij de projectonderdelen, dus ze is daarna ook
              daar niet meer kiesbaar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={isDeleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {isDeleting ? 'Verwijderen...' : 'Verwijderen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
